import type { ColumnDefProps } from '@components/shared/Table/types'
import type { GroupedRowData } from '@components/shared/TableGrouped/types'
import { IconButton } from '@mui/material'
import { CheckCircle } from '@phosphor-icons/react'
import { EditIcon } from '@assets/svgs/EditIcon'
import { currencyMask } from '@utils/masks/currencyMask'
import { getAccountCodeForDisplay } from './accountingAccountMapping'
import { getGroupIcon } from './getGroupIcon'
import { getGroupTooltip, GroupInfoTooltip } from './groupTooltips'
import { PROFILES_EDIT_STOCK_OPTION } from '@pages/Preview/helpers/previewGridValidationActions'
import type { CSSProperties } from 'react'
import type { DetailedSummaryResponseProps, DetailedSummaryGroupProps, DetailedSummaryItemProps } from '../context/types'

const STOCK_OPTION_DESCRIPTION = 'Stock Option'

// Grupos onde não exibimos o lápis de "atualizado pelo sistema" (ApData), mantendo apenas o checkmark
const HIDE_APDATA_EDIT_GROUP_CODES = ['SAL', 'BEN', 'IND']

interface ParseDetailedSummaryPeopleProps {
  data: DetailedSummaryResponseProps | null
  onEdit: (item: DetailedSummaryItemProps, group: DetailedSummaryGroupProps) => void
  canEdit?: boolean
  profileName?: string
}

const actionsCellGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  width: '100%'
}

const createActionsColumn = (
  item: DetailedSummaryItemProps,
  group: DetailedSummaryGroupProps,
  onEdit: (item: DetailedSummaryItemProps, group: DetailedSummaryGroupProps) => void,
  canEdit: boolean = false,
  isGroup: boolean = false,
  profileName?: string
): ColumnDefProps => {
  const isStockOption = item.description === STOCK_OPTION_DESCRIPTION
  const effectiveCanEdit = isStockOption
    ? (profileName ? PROFILES_EDIT_STOCK_OPTION.includes(profileName) : false)
    : canEdit

  const renderValue = () => {
    if (isGroup) return null

    // Atualizado pelo sistema (ApData): lápis desabilitado + checkmark verde
    // Nos grupos Salários (SAL) e Benefícios (BEN) o lápis é omitido, mantendo apenas o checkmark
    if (item.dataSource === 'apdata') {
      const hideEditIcon = HIDE_APDATA_EDIT_GROUP_CODES.includes(group.code)

      // Sem o lápis, o checkmark é centralizado na célula
      if (hideEditIcon) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <CheckCircle size={16} weight="fill" color="#38A169" />
          </div>
        )
      }

      return (
        <div style={actionsCellGridStyle}>
          <span />
          <IconButton size="small" disabled sx={{ color: '#A0AEC0' }}>
            <EditIcon />
          </IconButton>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '2px' }}>
            <CheckCircle size={16} weight="fill" color="#38A169" />
          </div>
        </div>
      )
    }

    // Para "Stock Option" o acesso é definido pelo perfil (VP_BU), ignorando allowEdit do backend
    // Para os demais itens, allowEdit é o portão principal e o perfil restringe adicionalmente
    const canShow = isStockOption ? effectiveCanEdit : (item.isEditable && effectiveCanEdit)
    if (!canShow) return null

    return (
      <div style={actionsCellGridStyle}>
        <span />
        <IconButton
          size="small"
          onClick={() => onEdit(item, group)}
          sx={{ color: '#2A4365' }}
        >
          <EditIcon />
        </IconButton>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '2px' }}>
          {/* Bolinha laranja: indica que o item foi editado manualmente */}
          {item.dataSource === 'manual' && (
            <span
              style={{
                display: 'inline-block',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#E87722',
                flexShrink: 0
              }}
            />
          )}
        </div>
      </div>
    )
  }

  return {
    id: 'action',
    columnName: 'Ações',
    align: 'center',
    minWidth: '5rem',
    maxWidth: '5rem',
    stickyPosition: 'right',
    withoutTooltip: true,
    value: renderValue()
  }
}

const createItemRow = (
  item: DetailedSummaryItemProps,
  group: DetailedSummaryGroupProps,
  onEdit: (item: DetailedSummaryItemProps, group: DetailedSummaryGroupProps) => void,
  canEdit: boolean = false,
  profileName?: string
): ColumnDefProps[] => {
  return [
    {
      id: 'group',
      columnName: 'Grupo',
      align: 'left',
      headerAlign: 'center',
      value: '',
      sortable: true,
      minWidth: '18rem'
    },
    {
      id: 'description',
      columnName: 'Descrição',
      align: 'left',
      headerAlign: 'center',
      value: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ fontSize: '14px', fontWeight: 500, color: '#2D3748' }}>
            {item.description || '-'}
          </div>
          <div style={{ fontSize: '12px', color: '#718096' }}>
            {getAccountCodeForDisplay(item.description, item.accountCode)}
          </div>
        </div>
      ),
      sortable: true,
      minWidth: '30rem'
    },
    {
      id: 'amount',
      columnName: 'Valor (R$)',
      align: 'right',
      headerAlign: 'center',
      value: item.amount !== undefined && item.amount !== null && Number(item.amount) !== 0 ? currencyMask(Number(item.amount).toFixed(2)) : '-',
      sortable: true,
      minWidth: '15rem'
    },
    createActionsColumn(item, group, onEdit, canEdit, false, profileName)
  ]
}

const createGroupRow = (group: DetailedSummaryGroupProps): ColumnDefProps[] => {
  const icon = group.icon || getGroupIcon(group.code)
  // Tooltip único do agrupamento: fonte do dado, referência de atualização e editabilidade
  const groupTooltip = getGroupTooltip(group.code, group.name)

  return [
    {
      id: 'group',
      columnName: 'Grupo',
      align: 'left',
      headerAlign: 'center',
      // Desliga o tooltip genérico da célula para não competir com o tooltip informativo do grupo
      withoutTooltip: true,
      value: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {icon}
          <span>{group.name}</span>
          <GroupInfoTooltip title={groupTooltip} />
        </div>
      ),
      sortable: false,
      minWidth: '18rem'
    },
    {
      id: 'description',
      columnName: 'Descrição',
      align: 'left',
      headerAlign: 'center',
      value: '',
      sortable: false,
      minWidth: '30rem'
    },
    {
      id: 'amount',
      columnName: 'Valor (R$)',
      align: 'right',
      headerAlign: 'center',
      value: group.subtotal !== undefined && group.subtotal !== null && Number(group.subtotal) !== 0 ? currencyMask(Number(group.subtotal).toFixed(2)) : '-',
      sortable: false,
      minWidth: '15rem'
    },
    {
      id: 'action',
      columnName: 'Ações',
      align: 'center',
      headerAlign: 'center',
      minWidth: '5rem',
      maxWidth: '5rem',
      stickyPosition: 'right',
      withoutTooltip: true,
      value: null
    }
  ]
}

const processGroups = (
  groups: DetailedSummaryGroupProps[],
  onEdit: (item: DetailedSummaryItemProps, group: DetailedSummaryGroupProps) => void,
  canEdit: boolean = false,
  profileName?: string
): GroupedRowData[] => {
  return groups.map((group, groupIndex) => {
    const uniqueGroupId = `${groupIndex}-${group.code}`

    const groupedRow: GroupedRowData = {
      id: uniqueGroupId,
      isParent: true,
      row: createGroupRow(group)
    }

    if (group.items && group.items.length > 0) {
      groupedRow.children = group.items.map((item, itemIndex) => {
        const uniqueItemId = `${uniqueGroupId}-${itemIndex}-${item.id}`

        return {
          id: uniqueItemId,
          isParent: false,
          row: createItemRow(item, group, onEdit, canEdit, profileName)
        }
      })
    }

    return groupedRow
  })
}

export const parseDetailedSummaryPeople = ({
  data,
  onEdit,
  canEdit = false,
  profileName
}: ParseDetailedSummaryPeopleProps): GroupedRowData[] => {
  if (!data || !data.groups || data.groups.length === 0) {
    return []
  }

  return processGroups(data.groups, onEdit, canEdit, profileName)
}
